import { Doughnut } from 'react-chartjs-2';
import { Panel, SkeletonLine, capitalize } from '@crazyegginc/hatch';
import classNames from 'classnames';
import { abbreviateNumber } from '/src/utils';

import { PanelHeader } from './PanelHeader';

export function DonutPanel({
  title,
  subtitle,
  dimension,
  data,
  isLoading = false,
  size = `120px`,
  hasDetails = false,
  skeletonClassName,
}) {
  const total =
    (data?.datasets ?? [])?.[0]?.data?.reduce?.((sum, value) => {
      return sum + value;
    }, 0) ?? 0;

  const largestValue =
    (data?.datasets ?? [])?.[0]?.data?.reduce?.((current, value) => {
      if (value > current) {
        return value;
      }
      return current;
    }, 0) ?? 0;

  const chartData =
    total === 0
      ? {
          labels: ['Empty'],
          datasets: [
            {
              data: [1],
              backgroundColor: ['#e1e6ef'],
              borderColor: '#fff',
              borderWidth: 0,
              cutout: '75%',
              hoverOffset: 0,
              hoverBorderColor: '#fff',
              hoverBackgroundColor: ['#e1e6ef'],
            },
          ],
        }
      : data;

  return (
    <li data-testid="donut-panel">
      <Panel variant="secondary" secondaryHover={true} className="w-full h-full">
        <PanelHeader slug={dimension} title={title} subTitle={subtitle} disabled={!dimension || !hasDetails} />
        <div className="flex-1 self-center">
          <div className="relative" style={{ width: size, height: size }}>
            {isLoading ? (
              <SkeletonLine className={skeletonClassName} style={{ width: size, height: size }} />
            ) : (
              <>
                <Doughnut
                  data={chartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        enabled: false,
                      },
                    },
                  }}
                />
                <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex flex-col items-center">
                  <span className="font-bold text-[26px] leading-none text-black-pearl-500">
                    {abbreviateNumber(total).toLowerCase()}
                  </span>
                  <span className="text-graphite-shadow-500 text-xs leading-none">{subtitle}</span>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col w-full text-charcoal-gray-500 text-sm mt-3 divide-y divide-[#979797] divide-opacity-20">
          {isLoading ? (
            <>
              <SkeletonLine className="w-full" />
              <SkeletonLine />
            </>
          ) : (
            <table className="w-full table-auto">
              <tbody>
                {(data?.datasets?.[0]?.data ?? []).map((dataValue, idx, arr) => {
                  const percentage = dataValue && total ? `${((dataValue / total) * 100).toFixed(1)}%` : `0%`;
                  const isBold = dataValue === largestValue;
                  const backgroundColor = data?.datasets?.[0]?.backgroundColor?.[idx];
                  const label = data.labels?.[idx];
                  const isLast = idx === arr.length - 1;

                  return (
                    <tr
                      key={`${title.toLowerCase().replaceAll(' ', '_')}+${idx}`}
                      className={classNames({
                        'font-bold !text-black-pearl-500': isBold,
                        'border-b border-[#979797]/20': !isLast,
                      })}
                    >
                      <td className="truncate w-3/5 py-[7px]">
                        <span className="leading-none truncate">
                          <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor }} />
                          <span>{capitalize(label)}</span>
                        </span>
                      </td>
                      <td className="text-right py-[7px]">
                        <span className="leading-none">{percentage}</span>
                      </td>
                      <td className="text-right py-[7px]">
                        <span className="leading-none">{abbreviateNumber(dataValue).toLowerCase()}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Panel>
    </li>
  );
}
